import { z } from 'zod';

// A workflow is an ordered list of steps, each one a form. A step can carry a condition over the values of earlier
// steps. Conditions are structured data checked by a closed set of operators, never an expression string, and the
// schemas are strict so nothing else can be smuggled in. A condition that cannot be evaluated stops the flow with an
// error; it never skips or advances silently. A step whose condition reads a step that was itself skipped is
// skipped too, so an empty value from a step that never ran is never mistaken for an answer.
//
// Values are compared by field type, with no coercion: text, textarea, select and radio as strings, checkbox as
// true or false, multiselect by membership (contains, not_contains) or as a whole by is_empty.

const scalarSchema = z.union([z.string(), z.number(), z.boolean()]);

// The step a condition reads from, by its identifier, and the field of that step's form.
const subject = {
  step: z.uuid(),
  field: z.string().min(1),
};

export const stepPredicateSchema = z.discriminatedUnion('op', [
  z.strictObject({ ...subject, op: z.literal('equals'), value: scalarSchema }),
  z.strictObject({ ...subject, op: z.literal('not_equals'), value: scalarSchema }),
  z.strictObject({ ...subject, op: z.literal('in'), values: z.array(scalarSchema).min(1) }),
  z.strictObject({ ...subject, op: z.literal('not_in'), values: z.array(scalarSchema).min(1) }),
  // Membership tests for a multiselect field: whether one option is among the selected values.
  z.strictObject({ ...subject, op: z.literal('contains'), value: scalarSchema }),
  z.strictObject({ ...subject, op: z.literal('not_contains'), value: scalarSchema }),
  z.strictObject({ ...subject, op: z.literal('is_empty') }),
  z.strictObject({ ...subject, op: z.literal('is_not_empty') }),
]);

/** The step applies when every predicate holds. */
export const stepConditionSchema = z.strictObject({
  all: z.array(stepPredicateSchema).min(1),
});

export const workflowStepSchema = z.object({
  identifier: z.uuid(),
  /** The identifier of the form this step runs. */
  formId: z.string().min(1),
  /** 1-based order within the workflow. */
  position: z.int().min(1),
  condition: stepConditionSchema.nullable(),
});

export type StepPredicate = z.infer<typeof stepPredicateSchema>;
export type StepCondition = z.infer<typeof stepConditionSchema>;
export type WorkflowStep = z.infer<typeof workflowStepSchema>;
